// ============================================
// pages/transportation/TransporterPaymentsLedger.tsx
// Read-only payment rollup per contractor, reading from transport_trips + transport_bills.
// ============================================
import React, { useState, useEffect, useMemo } from 'react';
import { Wallet, Loader2, XCircle, Download } from 'lucide-react';
import {
  seasonsAPI, htContractorsAPI, transportTripsAPI, transportBillsAPI,
} from '../../api/services';
import { formatAmount, num } from '../../types/markfed';
import type { Season } from '../../types/markfed';

interface Row {
  contractor_id: number;
  firm_name: string;
  pan_number?: string;
  total_trips: number;
  total_bills: number;
  total_weight_qtl: number;
  amount_claimed: number;
  amount_verified: number;
  tds_amount: number;
  payment_released: number;
  balance: number;
  settlement_pct: number;
}

export const TransporterPaymentsLedger: React.FC = () => {
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [selectedSeasonId, setSelectedSeasonId] = useState<number>(0);
  const [contractors, setContractors] = useState<any[]>([]);
  const [trips, setTrips] = useState<any[]>([]);
  const [bills, setBills] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const [s, c] = await Promise.all([seasonsAPI.list(), htContractorsAPI.list()]);
        const list: Season[] = Array.isArray(s.data) ? s.data : [];
        setSeasons(list);
        setContractors(c.data);
        const active = list.find((x) => x.is_active) || list[0];
        if (active) setSelectedSeasonId(active.id);
      } catch (err: any) {
        setError(err?.response?.data?.message || 'Failed to load');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!selectedSeasonId) return;
    (async () => {
      setLoading(true);
      try {
        const [t, b] = await Promise.all([
          transportTripsAPI.list(selectedSeasonId),
          transportBillsAPI.list(selectedSeasonId),
        ]);
        setTrips(Array.isArray(t.data) ? t.data : []);
        setBills(Array.isArray(b.data) ? b.data : []);
      } catch (err: any) {
        setError(err?.response?.data?.message || 'Failed to load');
      } finally {
        setLoading(false);
      }
    })();
  }, [selectedSeasonId]);

  const rows = useMemo((): Row[] => {
    const map: Record<number, Row> = {};
    for (const c of contractors) {
      map[c.id] = {
        contractor_id: c.id,
        firm_name: c.firm_name,
        pan_number: c.pan_number,
        total_trips: 0, total_bills: 0, total_weight_qtl: 0,
        amount_claimed: 0, amount_verified: 0, tds_amount: 0,
        payment_released: 0, balance: 0, settlement_pct: 0,
      };
    }
    // Prefer bills (they aggregate trips). Fall back to trips for contractors with no bills yet.
    for (const b of bills) {
      const r = map[b.contractor_id];
      if (!r) continue;
      r.total_bills++;
      r.total_trips += num(b.total_trips);
      r.total_weight_qtl += num(b.total_weight_qtl);
      r.amount_claimed += num(b.total_amount_claimed);
      r.amount_verified += num(b.total_amount_verified);
      r.tds_amount += num(b.tds_amount);
      r.payment_released += num(b.payment_released);
    }
    // Trips that aren't yet aggregated into a bill — add them as "claimed but no bill"
    const tripContractorsInBills = new Set(bills.map((b) => b.contractor_id));
    for (const t of trips) {
      if (tripContractorsInBills.has(t.contractor_id)) continue;
      const r = map[t.contractor_id];
      if (!r) continue;
      r.total_trips++;
      r.total_weight_qtl += num(t.weight_qtl);
      r.amount_claimed += num(t.amount_claimed);
      r.amount_verified += num(t.amount_verified);
      r.payment_released += num(t.payment_released);
    }
    return Object.values(map)
      .map((r) => {
        const base = r.amount_verified > 0 ? r.amount_verified : r.amount_claimed;
        r.balance = Math.max(+(base - r.payment_released - r.tds_amount).toFixed(2), 0);
        r.settlement_pct = base > 0 ? (r.payment_released / base) * 100 : 0;
        return r;
      })
      .filter((r) => r.total_trips > 0 || r.total_bills > 0)
      .sort((a, b) => b.amount_claimed - a.amount_claimed);
  }, [contractors, trips, bills]);

  const totals = useMemo(() => ({
    trips: rows.reduce((s, r) => s + r.total_trips, 0),
    bills: rows.reduce((s, r) => s + r.total_bills, 0),
    claimed: rows.reduce((s, r) => s + r.amount_claimed, 0),
    verified: rows.reduce((s, r) => s + r.amount_verified, 0),
    tds: rows.reduce((s, r) => s + r.tds_amount, 0),
    released: rows.reduce((s, r) => s + r.payment_released, 0),
    balance: rows.reduce((s, r) => s + r.balance, 0),
  }), [rows]);

  const scoreColor = (v: number) => v >= 95 ? 'text-green-700' : v >= 80 ? 'text-yellow-700' : 'text-red-700';
  const handlePrint = () => window.print();

  if (loading && rows.length === 0) {
    return <div className="max-w-7xl mx-auto flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-blue-600 animate-spin" /><span className="ml-2 text-gray-500">Loading...</span></div>;
  }

  return (
    <div className="max-w-7xl mx-auto print:max-w-none">
      <div className="mb-6 flex items-center justify-between print:hidden">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Wallet className="w-7 h-7 text-blue-600" />
            Transporter Payments
          </h1>
          <p className="text-sm text-gray-500 mt-1">Read-only ledger — rolls up transport trips and bills by contractor</p>
        </div>
        <button onClick={handlePrint}
          className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-800 text-white font-semibold rounded-lg text-sm">
          <Download className="w-4 h-4" />Print
        </button>
      </div>

      <div className="mb-4 max-w-xs print:hidden">
        <label className="block text-xs font-medium text-gray-600 mb-1">Season</label>
        <select value={selectedSeasonId} onChange={(e) => setSelectedSeasonId(parseInt(e.target.value))}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
          {seasons.map((s) => (<option key={s.id} value={s.id}>{s.season_name}</option>))}
        </select>
      </div>

      {error && <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm flex items-center gap-2"><XCircle className="w-4 h-4" />{error}</div>}

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <p className="text-xs text-gray-500">Total Verified</p>
          <p className="text-2xl font-bold text-gray-900">{formatAmount(totals.verified)}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <p className="text-xs text-gray-500">Released</p>
          <p className="text-2xl font-bold text-green-700">{formatAmount(totals.released)}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <p className="text-xs text-gray-500">TDS Withheld</p>
          <p className="text-2xl font-bold text-red-600">{formatAmount(totals.tds)}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <p className="text-xs text-gray-500">Pending Balance</p>
          <p className="text-2xl font-bold text-orange-600">{formatAmount(totals.balance)}</p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-xs">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 text-left font-semibold text-gray-600 uppercase">Contractor</th>
                <th className="px-3 py-3 text-right font-semibold text-gray-600 uppercase">Trips</th>
                <th className="px-3 py-3 text-right font-semibold text-gray-600 uppercase">Bills</th>
                <th className="px-3 py-3 text-right font-semibold text-gray-600 uppercase">Weight (Qtl)</th>
                <th className="px-3 py-3 text-right font-semibold text-gray-600 uppercase">Claimed</th>
                <th className="px-3 py-3 text-right font-semibold text-gray-600 uppercase">Verified</th>
                <th className="px-3 py-3 text-right font-semibold text-gray-600 uppercase">TDS</th>
                <th className="px-3 py-3 text-right font-semibold text-gray-600 uppercase">Released</th>
                <th className="px-3 py-3 text-right font-semibold text-gray-600 uppercase">Balance</th>
                <th className="px-3 py-3 text-right font-semibold text-gray-600 uppercase">Settled %</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.length === 0 ? (
                <tr><td colSpan={10} className="px-4 py-12 text-center text-gray-400">No transporter activity this season.</td></tr>
              ) : rows.map((r) => (
                <tr key={r.contractor_id} className="hover:bg-gray-50">
                  <td className="px-3 py-2">
                    <div className="font-medium text-gray-900">{r.firm_name}</div>
                    {r.pan_number && <div className="text-[10px] text-gray-400 font-mono">{r.pan_number}</div>}
                  </td>
                  <td className="px-3 py-2 text-right">{r.total_trips}</td>
                  <td className="px-3 py-2 text-right">{r.total_bills}</td>
                  <td className="px-3 py-2 text-right">{num(r.total_weight_qtl).toFixed(2)}</td>
                  <td className="px-3 py-2 text-right">{formatAmount(r.amount_claimed)}</td>
                  <td className="px-3 py-2 text-right font-medium">{formatAmount(r.amount_verified)}</td>
                  <td className="px-3 py-2 text-right text-red-600">{formatAmount(r.tds_amount)}</td>
                  <td className="px-3 py-2 text-right text-green-700">{formatAmount(r.payment_released)}</td>
                  <td className="px-3 py-2 text-right text-orange-600 font-bold">{formatAmount(r.balance)}</td>
                  <td className={`px-3 py-2 text-right font-semibold ${scoreColor(r.settlement_pct)}`}>{r.settlement_pct.toFixed(1)}%</td>
                </tr>
              ))}
            </tbody>
            {rows.length > 0 && (
              <tfoot className="bg-gray-50 border-t-2 border-gray-300">
                <tr className="font-bold">
                  <td className="px-3 py-2">TOTAL</td>
                  <td className="px-3 py-2 text-right">{totals.trips}</td>
                  <td className="px-3 py-2 text-right">{totals.bills}</td>
                  <td className="px-3 py-2"></td>
                  <td className="px-3 py-2 text-right">{formatAmount(totals.claimed)}</td>
                  <td className="px-3 py-2 text-right">{formatAmount(totals.verified)}</td>
                  <td className="px-3 py-2 text-right text-red-600">{formatAmount(totals.tds)}</td>
                  <td className="px-3 py-2 text-right text-green-700">{formatAmount(totals.released)}</td>
                  <td className="px-3 py-2 text-right text-orange-600">{formatAmount(totals.balance)}</td>
                  <td className="px-3 py-2 text-right">
                    {totals.verified > 0 ? ((totals.released / totals.verified) * 100).toFixed(1) : 0}%
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
};

export default TransporterPaymentsLedger;
